// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * The Satellites card in Tools → Layers (docs/AYNI_XR_SATELLITES_PLAN.md):
 * on/off, what to show, colour by orbit, country or purpose, a legend
 * whose entries hide and show, the satellites' own clock (live, or up to a
 * day either way at up to 1200×), trails, the data's age, the mandatory
 * attribution, and the latest space events.
 *
 * Renders into `#tools-menu-satellites`, which the phone's AR controls
 * lend as they lend the other Layers sections.
 */
import { t, tAttr, tHtml, type MessageKey } from '../i18n'
import { formatDate, formatNumber } from '../i18n/format'
import { escapeAttr, escapeHtml } from './domUtils'
import { SATELLITE_SPEEDS, SCRUB_LIMIT_MS } from '../services/satellites/satelliteClock'
import {
  COLOUR_BYS,
  MAP_HEIGHTS,
  SHOW_SETS,
  type MapHeight,
  type ColourBy,
  type SatelliteLayer,
  type ShowSet,
} from '../services/satellites/satelliteLayer'
import type { SatelliteEvent } from '../services/satellites/satelliteTypes'
import { categoryName, eventFacts, eventKindName, localised } from '../services/satellites/satelliteText'
import { platformName } from '../services/satellites/sourcePlatforms'

export const SATELLITE_SECTION_ID = 'tools-menu-satellites'

const SHOW_KEY: Record<ShowSet, MessageKey> = {
  highlights: 'satellites.show.highlights',
  all: 'satellites.show.all',
  starlink: 'satellites.show.starlink',
  'with-datasets': 'satellites.show.withDatasets',
  'fengyun-1c-debris': 'satellites.show.fengyun',
  'cosmos-2251-debris': 'satellites.show.cosmos',
  'iridium-33-debris': 'satellites.show.iridium',
}

const MAP_HEIGHT_KEY: Record<MapHeight, MessageKey> = {
  ground: 'satellites.mapHeight.ground',
  compact: 'satellites.height.compact',
  true: 'satellites.height.true',
}

const COLOUR_BY_KEY: Record<ColourBy, MessageKey> = {
  orbit: 'satellites.colourBy.orbit',
  country: 'satellites.colourBy.country',
  purpose: 'satellites.colourBy.purpose',
}

/** "+3 h 20 min" / "−45 min" from the clock's offset. */
export function offsetText(ms: number): string {
  const sign = ms < 0 ? '−' : '+'
  const mins = Math.round(Math.abs(ms) / 60_000)
  if (mins < 1) return ''
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return h ? `${sign}${h} h${m ? ` ${m} min` : ''}` : `${sign}${m} min`
}

export interface SatellitePanelHandle {
  render(): void
  destroy(): void
}

export function mountSatellitePanel(
  host: HTMLElement,
  layer: SatelliteLayer,
  opts: {
    lang: () => string
    onEvent?: (e: SatelliteEvent) => void
    /** In a VR/AR session: the browser globe's height row is left out. */
    inXr?: () => boolean
    /** Sky above me: a pass's dataset, loaded as Browse would (step 3 of the catalog plan). */
    onLoadDataset?: (id: string) => void
  } = { lang: () => 'en' },
): SatellitePanelHandle {
  let timeTimer: ReturnType<typeof setInterval> | null = null

  function timeLine(): string {
    const clock = layer.clock
    const at = clock.time()
    const when = formatDate(at, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'UTC', timeZoneName: 'short' })
    const off = offsetText(clock.offsetMs())
    return off ? t('satellites.time.offset', { time: when, offset: off }) : when
  }

  /** The folded card's one line: how many show, or what is happening. */
  function foldedLine(): string {
    if (layer.status === 'loading') return t('satellites.status.loading')
    if (layer.status === 'error') return t('satellites.status.error', { error: layer.error ?? '' })
    return t('satellites.status.count', { shown: formatNumber(layer.visibleCount()), total: formatNumber(layer.engine.count) })
  }

  function statusHtml(): string {
    if (layer.status === 'loading') return `<p class="sat-status" role="status">${tHtml('satellites.status.loading')}</p>`
    if (layer.status === 'error') return `<p class="sat-status sat-status-error" role="status">${escapeHtml(t('satellites.status.error', { error: layer.error ?? '' }))}</p>`
    if (layer.status !== 'ready' || !layer.pointer) return ''
    const asOf = formatDate(layer.pointer.updatedAt, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'UTC', timeZoneName: 'short' })
    return `
      <p class="sat-status" role="status">${escapeHtml(t('satellites.status.count', { shown: formatNumber(layer.visibleCount()), total: formatNumber(layer.engine.count) }))}
        · ${escapeHtml(t('satellites.status.asOf', { time: asOf }))}</p>
      ${layer.stale ? `<p class="sat-status sat-status-stale">${tHtml('satellites.status.stale')}</p>` : ''}`
  }

  function eventsHtml(): string {
    const list = layer.events.slice(0, 5)
    if (!list.length) return ''
    const lang = opts.lang()
    return `
      <div class="sat-events">
        <h5 class="sat-subtitle">${tHtml('satellites.events.title')}</h5>
        <ul class="sat-events-list">
          ${list
            .map(
              (e) => `
            <li><button type="button" class="sat-event" data-event="${escapeAttr(e.id)}" aria-current="${layer.eventFocus?.event.id === e.id}">
              <span class="sat-event-kind" data-kind="${escapeAttr(e.kind)}">${escapeHtml(eventKindName(e.kind))}</span>
              <span class="sat-event-date">${escapeHtml(formatDate(e.date, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }))}</span>
              <span class="sat-event-title">${escapeHtml(localised(e.title, lang))}</span>
            </button></li>`,
            )
            .join('')}
        </ul>
        ${eventDetailHtml(lang)}
      </div>`
  }

  /** The opened event, under the list: the same facts as the card on the globe, for the headset and phone too. */
  function eventDetailHtml(lang: string): string {
    const facts = eventFacts(layer, lang)
    if (!facts) return ''
    return `
        <div class="sat-event-detail" role="group" aria-label="${escapeAttr(facts.title)}">
          <button type="button" class="sat-event-detail-close" id="sat-event-close" aria-label="${tAttr('satellites.event.close')}">✕</button>
          <p class="sat-event-detail-title">${escapeHtml(facts.title)}</p>
          ${facts.text ? `<p class="sat-event-detail-text">${escapeHtml(facts.text)}</p>` : ''}
          ${facts.rows.length ? `<dl>${facts.rows.map(([k, v]) => `<dt>${escapeHtml(k)}</dt><dd>${escapeHtml(v)}</dd>`).join('')}</dl>` : ''}
          ${facts.note ? `<p class="sat-status">${escapeHtml(facts.note)}</p>` : ''}
          ${facts.source ? `<a href="${escapeAttr(facts.source)}" target="_blank" rel="noopener noreferrer">${tHtml('satellites.event.source')}</a>` : ''}
        </div>`
  }

  /**
   * AYNI (owner, 2026-10-23): the satellites the data on the globe came
   * from (DSA 1.10 platforms), one chip each; pressing one follows it.
   * One the GP mirror does not carry is named but cannot be followed.
   */
  function sourcesHtml(): string {
    const list = layer.sources
    if (!list.length) return ''
    const lang = opts.lang()
    const selected = layer.selectedInfo?.norad
    return `
      <div class="sat-sources" title="${tAttr('satellites.sources.tip')}">
        <h5 class="sat-subtitle">${tHtml('satellites.sources.title')}</h5>
        <div class="sat-sources-list">
          ${list
            .map(
              (s) =>
                `<button type="button" class="sat-chip sat-source" data-source="${s.norad}" data-tracked="${s.index >= 0}" data-standby="${s.standby}" data-dated="${s.dated}" aria-pressed="${selected === s.norad}"${s.index < 0 ? ` disabled title="${tAttr('satellites.sources.notTracked')}"` : s.standby ? ` title="${tAttr('satellites.sources.standby')}"` : s.dated ? ` title="${tAttr(s.platform.nominalLongitude !== null ? 'satellites.sources.datedNominal' : 'satellites.sources.dated')}"` : ''}>${escapeHtml(platformName(s.platform, lang))}</button>`,
            )
            .join('')}
        </div>
      </div>`
  }

  /** With the layer off: how many satellites the data comes from, if any. */
  function sourcesHint(): string {
    layer.refreshSources()
    const n = layer.sources.length
    return n ? `<p class="tools-layer-cap sat-sources-hint">${escapeHtml(t('satellites.sources.hint', { count: formatNumber(n) }))}</p>` : ''
  }

  function render(): void {
    const s = layer.settings
    const clock = layer.clock
    const legend = layer.legend()
    const offset = Math.round(clock.offsetMs() / 60_000)
    // Another body on the globe (owner, 2026-10-23): the switch is held and the card says why.
    const held = !layer.onEarth
    host.hidden = false
    host.innerHTML = `
      <div class="tools-layer-card sat-card" data-kind="satellites">
        <div class="tools-layer-head">
          <h4 class="tools-layer-title"><button type="button" class="sat-fold" id="sat-fold" aria-expanded="${!s.collapsed}" title="${tAttr(s.collapsed ? 'satellites.fold.open' : 'satellites.fold.close')}"><span class="sat-fold-caret" aria-hidden="true">${s.collapsed ? '▸' : '▾'}</span>${tHtml('satellites.title')}</button></h4>
          <button type="button" class="sat-switch" id="sat-on" role="switch" aria-checked="${s.on}" aria-label="${tAttr('satellites.toggle')}"${held ? ` disabled title="${tAttr('satellites.otherBody')}"` : ''}><span aria-hidden="true"></span></button>
        </div>
        ${
          held
            ? `<p class="tools-layer-cap sat-held" role="status">${tHtml('satellites.otherBody')}</p>`
            : s.on && s.collapsed
            ? `<p class="sat-status sat-folded">${escapeHtml(foldedLine())}</p>`
            : s.collapsed
              ? ''
              : s.on
            ? `
        <div class="sat-row" role="group" aria-label="${tAttr('satellites.show.label')}">
          <span class="sat-label">${tHtml('satellites.show.label')}</span>
          <div class="sat-chips">
            ${SHOW_SETS.map(
              (set) => `<button type="button" class="sat-chip" data-show="${set}" aria-pressed="${s.show.includes(set)}"${set === 'highlights' ? ` title="${tAttr('satellites.show.highlights.tip')}"` : set === 'with-datasets' ? ` title="${tAttr('satellites.show.withDatasets.tip')}"` : ''}>${tHtml(SHOW_KEY[set])}</button>`,
            ).join('')}
          </div>
        </div>
        <div class="sat-row" role="radiogroup" aria-label="${tAttr('satellites.colourBy.label')}">
          <span class="sat-label">${tHtml('satellites.colourBy.label')}</span>
          <div class="sat-seg">
            ${COLOUR_BYS.map((by) => {
              const off = by === 'country' && !layer.hasCatalogue
              return `<button type="button" role="radio" class="sat-seg-btn" data-by="${by}" aria-checked="${s.colourBy === by}"${off ? ` disabled title="${tAttr('satellites.colourBy.needsCatalogue')}"` : ''}>${tHtml(COLOUR_BY_KEY[by])}</button>`
            }).join('')}
          </div>
        </div>
        ${
          legend.length
            ? `
        <ul class="sat-legend" aria-label="${tAttr('satellites.legend.hint')}" title="${tAttr('satellites.legend.hint')}">
          ${legend
            .map(
              (e) => `<li><button type="button" class="sat-legend-item" data-cat="${escapeAttr(e.key)}" aria-pressed="${!e.hidden}">
                <span class="sat-swatch" style="background:${escapeAttr(e.colour)}" aria-hidden="true"></span>
                <span class="sat-legend-name">${escapeHtml(categoryName(s.colourBy, e.key))}</span>
                <span class="sat-legend-count">${escapeHtml(formatNumber(e.count))}</span>
              </button></li>`,
            )
            .join('')}
        </ul>`
            : ''
        }
        ${sourcesHtml()}
        <div class="sat-row sat-time" role="group" aria-label="${tAttr('satellites.time.label')}">
          <button type="button" class="sat-chip" id="sat-live" aria-pressed="${clock.live}">${tHtml('satellites.time.live')}</button>
          <button type="button" class="sat-chip" id="sat-play" aria-label="${tAttr(clock.playing ? 'satellites.time.pause' : 'satellites.time.play')}">${clock.playing ? '❚❚' : '▶'}</button>
          <select class="sat-speed" id="sat-speed" aria-label="${tAttr('satellites.time.speed')}">
            ${SATELLITE_SPEEDS.map((v) => `<option value="${v}"${clock.speed === v ? ' selected' : ''}>${v}×</option>`).join('')}
          </select>
          <label class="sat-trails"><input type="checkbox" id="sat-trails"${s.trails ? ' checked' : ''}> ${tHtml('satellites.trails')}</label>
          <label class="sat-trails" title="${tAttr('satellites.fov.tip')}"><input type="checkbox" id="sat-fov"${s.fov ? ' checked' : ''}> ${tHtml('satellites.fov')}</label>
        </div>
        <input type="range" class="sat-scrub" id="sat-scrub" min="${-SCRUB_LIMIT_MS / 60_000}" max="${SCRUB_LIMIT_MS / 60_000}" step="5" value="${offset}" aria-label="${tAttr('satellites.time.scrub')}">
        <p class="sat-when" id="sat-when" aria-live="off">${escapeHtml(timeLine())}</p>
        ${
          opts.inXr?.()
            ? ''
            : `
        <div class="sat-row" role="radiogroup" aria-label="${tAttr('satellites.mapHeight.label')}" title="${tAttr('satellites.mapHeight.tip')}">
          <span class="sat-label">${tHtml('satellites.mapHeight.label')}</span>
          <div class="sat-seg">
            ${MAP_HEIGHTS.map((h) => `<button type="button" role="radio" class="sat-seg-btn" data-mh="${h}" aria-checked="${s.mapHeight === h}">${tHtml(MAP_HEIGHT_KEY[h])}</button>`).join('')}
          </div>
        </div>`
        }
        <div class="sat-row" role="radiogroup" aria-label="${tAttr('satellites.height.label')}" title="${tAttr('satellites.height.tip')}">
          <span class="sat-label">${tHtml('satellites.height.label')}</span>
          <div class="sat-seg">
            <button type="button" role="radio" class="sat-seg-btn" data-alt="true" aria-checked="${s.altitude === 'true'}">${tHtml('satellites.height.true')}</button>
            <button type="button" role="radio" class="sat-seg-btn" data-alt="compact" aria-checked="${s.altitude === 'compact'}">${tHtml('satellites.height.compact')}</button>
          </div>
        </div>
        ${
          layer.skyAvailable
            ? `
        <div class="sat-row">
          <button type="button" class="sat-chip" id="sat-sky" aria-pressed="${layer.skyOn}" title="${tAttr('satellites.sky.tip')}">${tHtml('satellites.sky.button')}</button>
          ${layer.skyOn ? `<button type="button" class="sat-chip" id="sat-north">${tHtml('satellites.sky.setNorth')}</button>` : ''}
          <button type="button" class="sat-chip" id="sat-full-vr" aria-pressed="${layer.fullVrOn}" title="${tAttr('satellites.sky.fullVrTip')}">${tHtml('satellites.sky.fullVr')}</button>
        </div>
        ${layer.skyOn && layer.skyStatus ? `<p class="sat-status sat-sky-status" role="status">${escapeHtml(layer.skyStatus).replace(/\n/g, '<br>')}</p>` : ''}
        ${layer.fullVrOn ? `<p class="sat-status sat-sky-status">${tHtml('satellites.sky.fullVrCredit')}</p>` : ''}
        ${
          layer.skyOn && layer.skyPasses.length
            ? `<div class="sat-passes"><h5 class="sat-subtitle">${tHtml('satellites.passes.title')}</h5><ul class="sat-map-card-datasets">${layer.skyPasses
                .map(
                  (p) => `<li><span class="sat-map-card-dataset-title">${escapeHtml(p.text)}</span>${p.datasets[0] && opts.onLoadDataset ? `<button type="button" class="sat-chip sat-map-card-load" data-load="${escapeAttr(p.datasets[0])}">${tHtml('satellites.card.load')}</button>` : ''}</li>`,
                )
                .join('')}</ul></div>`
            : ''
        }`
            : ''
        }
        ${statusHtml()}
        ${eventsHtml()}
        <p class="sat-credit">${escapeHtml(layer.attribution ?? '')} ${tHtml('satellites.approximate')}</p>`
            : `<p class="tools-layer-cap">${tHtml('satellites.note')}</p>${sourcesHint()}`
        }
      </div>`
  }

  function onClick(ev: Event): void {
    const el = (ev.target as HTMLElement).closest('button')
    if (!el || !host.contains(el)) return
    if (el.id === 'sat-on') {
      if (layer.onEarth) layer.setOn(!layer.on)
    } else if (el.dataset.show) layer.toggleShow(el.dataset.show as ShowSet)
    else if (el.dataset.by) layer.update({ colourBy: el.dataset.by as ColourBy })
    else if (el.dataset.cat) layer.toggleHidden(el.dataset.cat)
    else if (el.dataset.source) {
      const norad = Number(el.dataset.source)
      layer.selectNorad(layer.selectedInfo?.norad === norad ? null : norad)
    }
    else if (el.id === 'sat-live') layer.clock.goLive()
    else if (el.id === 'sat-play') layer.clock.setPlaying(!layer.clock.playing)
    else if (el.id === 'sat-event-close') layer.closeEvent()
    else if (el.id === 'sat-fold') layer.update({ collapsed: !layer.settings.collapsed })
    else if (el.dataset.mh) layer.update({ mapHeight: el.dataset.mh as MapHeight })
    else if (el.dataset.alt) layer.update({ altitude: el.dataset.alt === 'compact' ? 'compact' : 'true' })
    else if (el.id === 'sat-sky') layer.setSky(!layer.skyOn)
    else if (el.id === 'sat-north') layer.requestNorth()
    else if (el.id === 'sat-full-vr') layer.setFullVr(!layer.fullVrOn)
    else if (el.dataset.load) {
      opts.onLoadDataset?.(el.dataset.load)
      return
    }
    else if (el.dataset.event) {
      const e = layer.events.find((x) => x.id === el.dataset.event)
      if (e) opts.onEvent?.(e)
    } else return
    render()
  }

  function onInput(ev: Event): void {
    const el = ev.target as HTMLInputElement | HTMLSelectElement
    if (el.id === 'sat-scrub') {
      layer.clock.seek(Date.now() + Number(el.value) * 60_000)
      const when = host.querySelector('#sat-when')
      if (when) when.textContent = timeLine()
    }
  }

  function onChangeEv(ev: Event): void {
    const el = ev.target as HTMLInputElement | HTMLSelectElement
    if (el.id === 'sat-speed') {
      layer.clock.setSpeed(Number(el.value))
      render()
    } else if (el.id === 'sat-trails') {
      layer.update({ trails: (el as HTMLInputElement).checked })
    } else if (el.id === 'sat-fov') {
      layer.update({ fov: (el as HTMLInputElement).checked })
    } else if (el.id === 'sat-scrub') render()
  }

  host.addEventListener('click', onClick)
  host.addEventListener('input', onInput)
  host.addEventListener('change', onChangeEv)
  const offLayer = layer.onChange(() => {
    // Don't rebuild under a dragging thumb.
    if (document.activeElement?.id === 'sat-scrub') return
    render()
  })
  const offClock = layer.clock.onChange(() => {
    if (document.activeElement?.id !== 'sat-scrub') render()
  })
  // The time line and scrubber follow the clock while the card is open.
  timeTimer = setInterval(() => {
    if (!layer.on || host.hidden || !host.isConnected) return
    const when = host.querySelector('#sat-when')
    if (when) when.textContent = timeLine()
    const scrub = host.querySelector<HTMLInputElement>('#sat-scrub')
    if (scrub && document.activeElement !== scrub) scrub.value = String(Math.round(layer.clock.offsetMs() / 60_000))
  }, 1000)
  render()

  return {
    render,
    destroy() {
      host.removeEventListener('click', onClick)
      host.removeEventListener('input', onInput)
      host.removeEventListener('change', onChangeEv)
      offLayer()
      offClock()
      if (timeTimer) clearInterval(timeTimer)
    },
  }
}
